// src/utils/request.js
import axios from 'axios'
import {ElMessage, ElMessageBox} from 'element-plus'
import router from '@/router'
import { useUserStore } from '@/store/modules/userStore';
import pinia from '@/store/index.js';

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL

const service = axios.create({
  baseURL: API_BASE_URL || '', // 清空默认baseURL，避免重复拼接
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json;charset=utf-8'
  }
})

let refreshPromise = null;

// 请求拦截器
service.interceptors.request.use(
  (config) => {
    // const pinia = require('@/store/index.js').default;
    const userStore = useUserStore(pinia);
    const accessToken = userStore.accessToken || sessionStorage.getItem('accessToken');
    if (accessToken && typeof accessToken === 'string') {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    console.error('请求拦截器错误：', error);
    return Promise.reject(error);
  }
);

// 刷新AccessToken的方法
const refreshAccessToken = async () => {
  // 若已有刷新请求在进行，直接返回该 Promise，避免重复请求
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    return Promise.reject(new Error('无刷新令牌'));
  }

  // 封装刷新请求为 Promise，赋值给 refreshPromise
  refreshPromise = new Promise((resolve, reject) => {
    // 定义内部异步函数，承载 await 逻辑
    const doRefresh = async () => {
      try {
        // 调用后端刷新 Token 接口（使用原始 axios，避免被当前拦截器拦截）
        const res = await axios.post(
          `${API_BASE_URL}/users/refreshToken`, // 拼接完整基础路径，避免404
          { refreshToken },
          { headers: { 'Content-Type': 'application/json;charset=utf-8' } }
        );

        let newAccessToken = res.data.data.accessToken;
        let newRefreshToken = res.data.data.refreshToken;
        if (typeof newAccessToken === 'object' && newAccessToken.accessToken) {
          newAccessToken = newAccessToken.accessToken;
        }
        if (!newAccessToken || typeof newAccessToken !== 'string') {
          throw new Error('刷新 Token 失败，未返回有效 AccessToken');
        }
        if (!newRefreshToken || typeof newRefreshToken !== 'string') {
          throw new Error('刷新 Token 失败，未返回有效 RefreshToken');
        }

        // 同步更新 Pinia 全局状态与本地存储
        // const pinia = require('@/store/index.js').default;
        localStorage.setItem('accessToken', newAccessToken);
        localStorage.setItem('refreshToken', newRefreshToken);
        const userStore = useUserStore(pinia);
        userStore.accessToken = newAccessToken;
        userStore.refreshToken = newRefreshToken;

        resolve(newAccessToken);
      } catch (error) {
        console.error('【刷新Token】失败:', error);
        // 刷新失败：清空所有状态
        // const pinia = require('@/store/index.js').default;
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('userInfo');
        const userStore = useUserStore(pinia);
        userStore.resetUserStatus();

        reject(error);
      } finally {
        // 刷新请求完成，清空 refreshPromise
        refreshPromise = null;
      }
    };
    // 调用内部异步函数，启动刷新流程
    doRefresh();
  });
  return refreshPromise;
};

// 响应拦截器：统一处理错误
service.interceptors.response.use(
  (response) => {
    // 统一处理响应数据
    const res = response.data;
    const successCodes = [0, 200];
    if (res.code && !successCodes.includes(res.code)) {
      ElMessage.error(res.msg || '请求失败');
      return Promise.reject(res);
    }
    return res;
  },
  async (error) => {
    console.error('响应拦截器错误：', error);
    const originalRequest = error.config;

    // 处理 401 Token 失效
    if (error.response?.status === 401) {

      const isLoginRequest = originalRequest.url.includes('login');
      const localRefreshToken = localStorage.getItem('refreshToken');
      const skipAuthAlert = originalRequest._noAuthAlert;  // 检查是否需要跳过登录失效弹框

      if (isLoginRequest || !localRefreshToken) {
        // 这是登录失败，或者彻底没登录，直接把错误抛回给页面
        return Promise.reject(error);
      }

      // 标记已重试，避免死循环（防止刷新 Token 失败后再次重试）
      if (originalRequest._retry) {
        // 如果设置了跳过登录失效弹框，不弹框直接返回错误
        if (skipAuthAlert) {
          return Promise.reject(error);
        }
        // 重试失败，弹窗提示（优化：关闭弹窗后重置全局状态）
        ElMessageBox.confirm(
          '当前登录状态已失效，请重新登录',
          '登录状态失效',
          {
            confirmButtonText: '去登录',
            cancelButtonText: '暂不登录',
            type: 'warning',
            closeOnClickModal: false // 禁止点击空白处关闭
          }
        ).then(() => {
          // 跳登录页，记录当前页面以便返回
          router.push({
            path: '/login',
            query: { redirect: router.currentRoute.fullPath }
          });
        }).catch(() => {
          // 暂不登录，跳首页
          router.push('/');
        });
        return Promise.reject(error);
      }

      // 标记为已重试，防止死循环
      originalRequest._retry = true;
      try {
        // 尝试刷新 Token（使用优化后的 refreshAccessToken 方法）
        console.log('【响应拦截器】开始刷新Token...');
        const newAccessToken = await refreshAccessToken();
        console.log('【响应拦截器】Token刷新成功，新token:', newAccessToken);
        // 用新 Token 重发原请求（更新请求头，保持请求配置不变）
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return service(originalRequest);
      } catch (refreshError) {
        console.error('【响应拦截器】Token刷新失败:', refreshError);
        // 如果设置了跳过登录失效弹框，不弹框直接返回错误
        if (originalRequest._noAuthAlert) {
          return Promise.reject(refreshError);
        }
        // 刷新失败，弹窗提示（无需重复清空状态，refreshAccessToken 中已处理）
        ElMessageBox.confirm(
          '当前登录状态已失效，请重新登录',
          '登录状态失效',
          {
            confirmButtonText: '去登录',
            cancelButtonText: '暂不登录',
            type: 'warning',
            closeOnClickModal: false
          }
        ).then(() => {
          router.push({
            path: '/login',
            query: { redirect: router.currentRoute.fullPath }
          });
        }).catch(() => {
          router.push('/');
        });
        return Promise.reject(refreshError);
      }
    }

    // 处理 400 错误（参数错误、登录失效、业务错误等）
    if (error.response?.status === 400) {
      const resData = error.response?.data;
      // 如果后端返回了业务错误信息，显示给用户
      if (resData?.msg) {
        ElMessage.error(resData.msg);
      } else {
        ElMessage.error('请求参数错误，请重试');
      }
      return Promise.reject(error);
    }

    // 其他错误处理
    ElMessage.error(error.message || '服务器请求失败');
    return Promise.reject(error);
  }
);
// 仅导出axios实例（避免封装层导致的参数传递问题）
export default service
